'use client';

import React, {FC, useState} from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {useRouter, useSearchParams} from 'next/navigation';

import {Input} from '@zoefin/design-system';
import {Button} from '@zoefin/design-system/v2';
import {logger} from '@zoefin/logger';
import {request} from '@zoefin/request';

import {getAffiliateLogo} from '@/utils/themeUtils';
import {redirectToDashboard} from '@/utils/userUtils';

import styles from './styles.module.scss';

type LoginState = {
  email: string;
  password: string;
};

type ErrorType = 'email';

interface LoginFormProps {
  host: string;
}

const LoginForm: FC<LoginFormProps> = ({host}) => {
  const {push} = useRouter();
  const searchParams = useSearchParams();
  const queryParams = Object.fromEntries(searchParams ?? new URLSearchParams());

  const [loginState, setLoginState] = useState<LoginState>({
    email: '',
    password: '',
  });

  const [isValidEmail, setIsValidEmail] = useState<boolean>(false);
  const [hasError, setHasError] = useState<ErrorType>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  const logo = getAffiliateLogo(host);

  const handleEmailChange = (value: string) => {
    setHasError(undefined);
    setLoginState({...loginState, email: value});
  };

  const handleSubmit = () => {
    setLoading(true);
    request<CheckUserStatusResponse>(`/api/portal/check-user-status`, {
      method: 'POST',
      body: {email: loginState.email},
    })
      .then((res) => {
        if (!res.userExists) {
          setHasError('email');
          setLoading(false);

          return;
        }

        if (res.is_in_plutus) {
          // User exists in Core and is in Plutus - redirect to dashboard app
          redirectToDashboard(loginState.email);

          return;
        }

        // Create portal session and redirect
        createPortalSession(loginState.email);
      })
      .catch(() => {
        setHasError('email');
        setLoading(false);
      });
  };

  const createPortalSession = async (email: string) => {
    const redirectUrl = queryParams.returnTo || '/dashboard/advisors';

    try {
      const response = await request<{success?: boolean}>(
        '/api/portal/create-session',
        {
          method: 'POST',
          body: {email},
        },
      );

      if (response.success) {
        push(redirectUrl);
      } else {
        setHasError('email');
        setLoading(false);
      }
    } catch (error) {
      logger.error({error}, 'Error creating portal session');
      setHasError('email');
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginForm}>
      <div className={styles.loginForm__logo}>
        <Image src={logo} alt={host} width={150} height={60} />
      </div>
      <div className={styles.loginForm__content}>
        <div className={styles.loginForm__content_title}>
          <h3>Welcome back!</h3>
          <p>Enter your email to see your advisor matches.</p>
        </div>
        <div className={styles.loginForm__content_form}>
          <Input
            id="email-input"
            type="email"
            label="Email"
            placeholder="Type..."
            value={loginState.email}
            validator={(_newVal, isValid) => isValid}
            onValidationChange={(isValid) => {
              setIsValidEmail(isValid);
            }}
            onChange={handleEmailChange}
            onKeyUp={(code) => {
              if (code === 'Enter' && isValidEmail) {
                handleSubmit();
              }
            }}
            errorMessage={
              hasError === 'email'
                ? 'This email is not registered. Please get started below.'
                : ''
            }
            hasError={hasError === 'email'}
            fitParent
            required
          />
          <Button
            disabled={!isValidEmail}
            loading={loading}
            onClick={handleSubmit}
            fullWidth
            as="button">
            Continue
          </Button>
        </div>
      </div>
      <div className={styles.loginForm__footer}>
        <p>
          Not a user yet? <Link href={'/onboarding'}> Get Started</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
